// Castintech dashboard — EFQM view modülü.
// Aşama F (2026-05-25) — EFQM kriterleri + RADAR + audit dosyaları.
// Aşama G G2/G3 (2026-05-29, BD-002) — CHARTER v1.4 § 1.5: 6 kriter (K6 eklendi),
//   efqm-gm (CQO) K5+K6 sahibi. 5→6 kriter drift fix.
//
// İçerik:
//   - 6 kriter kartı (başlık + sahip ajan + açıklama)
//   - RADAR framework açıklaması
//   - İlgili audit dosyaları (/castintech-api/wiki/files?tag=efqm endpoint)

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, $ } from '../utils.js';
import { createSkeleton } from '../ui/state-display.js';

interface EfqmFile {
  name: string;
  path: string;
  updated_at?: string;
}

interface EfqmFilesResponse {
  files: EfqmFile[];
}

// CHARTER v1.4 § 1.5 — 6 EFQM kriteri (K6 yeni, BD-2026-05-28-002).
// efqm-gm (CQO) K5+K6 sahibi; RADAR cycle owner.
const EFQM_CRITERIA = [
  {
    number: 1,
    title: 'Vizyon + Strateji',
    owner: 'Yılmaz + Vanta (CSO)',
    gem: '🎯',
    color: 'var(--gem-nexus, #818cf8)',
    description: 'Şirket yönü, ürün portföyü, 12-ay roadmap, yeni ürün ideation, stratejik yönetişim.',
    metrics: ['Aktif iş kolu sayısı', 'Yeni ürün/proje sayısı', '12-ay roadmap güncel mi?'],
  },
  {
    number: 2,
    title: 'Liderlik + Kültür',
    owner: 'Board + Nexus + Sentinel',
    gem: '🏛️',
    color: 'var(--gem-sentinel, #a8a29e)',
    description: 'GM hiyerarşi sağlığı, Board işleyişi (T1/T2/T3), etik, karar hızı, CHARTER uyumu, prosedür disiplini.',
    metrics: ['Board toplantısı yapıldı mı (T1/T2)?', 'Karar onay oranı (%)', 'Decision velocity (toplantı/hafta)'],
  },
  {
    number: 3,
    title: 'Paydaş Yönetimi',
    owner: 'Pearl (CCO) + Herald',
    gem: '💎',
    color: 'var(--gem-pearl, #94a3b8)',
    description: 'Müşteri, ajan, tedarikçi ilişkileri; onboarding, Pearl gate, dış iletişim dispatcher.',
    metrics: ['Aktif müşteri sayısı', 'Aktif prospect (Pearl gate)', 'Onboarding completion rate (%)'],
  },
  {
    number: 4,
    title: 'Süreç + Dönüşüm',
    owner: 'Forge/Pilot/Prism + Onyx + Lore',
    gem: '⚙️',
    color: 'var(--gem-prism, #818cf8)',
    description: 'SOP disiplini, deploy pipeline, model rotation, vault hijyeni, CI/CD güvenilirliği, middleware kalitesi.',
    metrics: ['Deploy başarı oranı (%)', 'CI test geçişi (%)', 'Skill/model drift sayısı'],
  },
  {
    number: 5,
    title: 'Sonuç + Performans',
    owner: 'efqm-gm (CQO) + Meter + Watchdog + SiteWatchdog',
    gem: '📊',
    color: 'var(--gem-efqm-gm, #A8E6CF)',
    description: 'KPI, audit, SLA, site currency, RADAR cycle, prosedür success/bypass, holding KPI çekirdeği (20 metrik).',
    metrics: ['Holding KPI snapshot (Pzt 08:00)', 'Prosedür success/bypass oranı', 'Watchdog alarm süresi (median)'],
  },
  {
    number: 6,
    title: 'Müşteri Memnuniyeti + Delivery Quality',
    owner: 'efqm-gm (CQO) + Pearl',
    gem: '⭐',
    color: 'var(--gem-efqm-gm, #A8E6CF)',
    description: 'NPS overall, CSAT @ T+7g, CES @ T+30g, delivery on-time-on-spec %, 8-katmanlı SOP gate, EFQM Recognition C2K (Y1).',
    metrics: ['NPS (genel)', 'CSAT (T+7g) + CES (T+30g)', 'Delivery on-time %'],
  },
];

let _efqmFiles: EfqmFile[] = [];
let _filesError: string | null = null;
let _filesLoaded = false;

export async function loadEfqm(): Promise<void> {
  // F4-A — İlk yüklemede audit dosya bölümü için skeleton göster (page açıksa).
  if (!_filesLoaded && state.currentPage === 'efqm') {
    const root = $('efqm-content');
    if (root && !root.querySelector('.efqm-layout')) {
      root.innerHTML = '';
      const sk = createSkeleton({ rows: 3, height: '60px', rowGap: '12px' });
      root.appendChild(sk);
    }
  }
  try {
    const r = await apiFetch('/castintech-api/wiki/files?path=wiki%2Faudits%2F&pattern=*efqm*');
    if (!r.ok) {
      throw new Error('wiki/files HTTP ' + r.status);
    }
    const d = await r.json() as EfqmFilesResponse;
    _efqmFiles = (d.files || []).sort((a, b) => b.name.localeCompare(a.name)).slice(0, 10);
    _filesError = null;
  } catch (e) {
    // F4-A — Önceki davranış: sessiz fail. Yeni: error mesajını UI'a yansıt.
    _filesError = (e as Error).message || 'Audit dosyaları yüklenirken sorun çıktı';
    _efqmFiles = [];
    console.warn('loadEfqm files fetch failed:', _filesError);
  } finally {
    _filesLoaded = true;
  }
  if (state.currentPage === 'efqm') renderEfqm();
}

export function renderEfqm(): void {
  const root = $('efqm-content');
  if (!root) return;

  const sub = $('efqm-sub');
  if (sub) sub.textContent = 'CHARTER v1.4 § 1.5 · 6 Kriter · RADAR · efqm-gm (CQO)';

  // 5 kriter kartı
  const criteriaHtml = EFQM_CRITERIA.map(c => `
    <div class="efqm-card" style="border-left: 3px solid ${c.color}">
      <div class="efqm-card-header">
        <span class="efqm-gem">${c.gem}</span>
        <span class="efqm-number" style="color:${c.color}">Kriter ${c.number}</span>
        <span class="efqm-title">${escHtml(c.title)}</span>
        <span class="efqm-owner">@${escHtml(c.owner)}</span>
      </div>
      <div class="efqm-desc">${escHtml(c.description)}</div>
      <div class="efqm-metrics">
        ${c.metrics.map(m => `<div class="efqm-metric-row">• ${escHtml(m)}</div>`).join('')}
      </div>
    </div>`).join('');

  // RADAR framework
  const radarHtml = `
    <div class="efqm-radar-box">
      <div class="efqm-radar-title">RADAR Değerlendirme Çerçevesi</div>
      <div class="efqm-radar-grid">
        <div class="efqm-radar-item">
          <div class="efqm-radar-letter">R</div>
          <div class="efqm-radar-label">Results</div>
          <div class="efqm-radar-desc">Hedefler vs. gerçekleşen (KPI sonuçları, trend, benchmark)</div>
        </div>
        <div class="efqm-radar-item">
          <div class="efqm-radar-letter">A</div>
          <div class="efqm-radar-label">Approach</div>
          <div class="efqm-radar-desc">Kullanılan yöntem — mantıklı, entegre, değer zinciriyle uyumlu?</div>
        </div>
        <div class="efqm-radar-item">
          <div class="efqm-radar-letter">D</div>
          <div class="efqm-radar-label">Deployment</div>
          <div class="efqm-radar-desc">Uygulamanın kapsamı — tüm ilgili alanlarda kullanılıyor mu?</div>
        </div>
        <div class="efqm-radar-item">
          <div class="efqm-radar-letter">A</div>
          <div class="efqm-radar-label">Assessment</div>
          <div class="efqm-radar-desc">Performans ölçümü, içgörü çıkarımı, öğrenme döngüsü</div>
        </div>
        <div class="efqm-radar-item">
          <div class="efqm-radar-letter">R</div>
          <div class="efqm-radar-label">Refinement</div>
          <div class="efqm-radar-desc">İyileştirme ve inovasyon — bir sonraki döngüye ne aktarılıyor?</div>
        </div>
      </div>
      <div style="font-size:11px;color:var(--muted);margin-top:8px">
        Sentinel haftalık brief + Nexus aylık rapor = RADAR döngüsü · CHARTER § 5.1
      </div>
    </div>`;

  // Audit dosyaları — F4-A: error, empty, list üç ayrı dal.
  let filesHtml: string;
  if (_filesError) {
    filesHtml = `<div class="ds-error" role="alert" style="margin:8px 0">
      <div class="ds-error-icon" aria-hidden="true">⚠️</div>
      <div class="ds-error-title">Audit dosyaları yüklenemedi</div>
      <div class="ds-error-desc">Bağlanırken sorun çıktı: ${escHtml(_filesError)}</div>
      <button class="ds-error-retry" type="button" id="efqm-retry-btn">Tekrar dene</button>
    </div>`;
  } else if (_efqmFiles.length) {
    filesHtml = `<div class="efqm-files">
        ${_efqmFiles.map(f => `
          <div class="efqm-file-row">
            <span class="efqm-file-icon">📄</span>
            <span class="efqm-file-name">${escHtml(f.name.replace(/\.md$/, ''))}</span>
            ${f.updated_at ? `<span class="efqm-file-date">${escHtml(f.updated_at.slice(0, 10))}</span>` : ''}
          </div>`).join('')}
       </div>`;
  } else {
    filesHtml = `<div class="ds-empty" role="status" style="margin:8px 0">
      <div class="ds-empty-icon" aria-hidden="true">📂</div>
      <div class="ds-empty-title">EFQM audit dosyası yok</div>
      <div class="ds-empty-desc">wiki/audits/*efqm*.md formatında eklenince burada görünür.</div>
    </div>`;
  }

  root.innerHTML = `
    <div class="efqm-layout">
      <div class="efqm-criteria">${criteriaHtml}</div>
      ${radarHtml}
      <div class="efqm-section">
        <div class="efqm-section-title">📂 İlgili Audit Dosyaları</div>
        ${filesHtml}
        <div style="margin-top:6px;font-size:11px;color:var(--muted)">wiki/audits/ + wiki/decisions/ — *efqm* içeren dosyalar</div>
      </div>
    </div>`;

  // F4-A — Audit dosya hata retry button bind.
  if (_filesError) {
    const btn = root.querySelector<HTMLButtonElement>('#efqm-retry-btn');
    if (btn) btn.addEventListener('click', () => { void loadEfqm(); });
  }
}
